<template>
    <BaseCard padding="lg" as="article" :class="showUnanswered ? 'ring-2 ring-inset ring-status-error' : ''" :style="accentStyle">
        <div v-if="question.title" class="details text-ink-muted mb-2">{{ question.title }}</div>
        <h2 class="body-bold text-ink leading-snug">
            {{ question.text }}
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- punctuation: the required marker; its accessible name is translated -->
            <span v-if="!question.is_optional" class="text-notification-error-text ml-1" :aria-label="t('components.assessment.required')">*</span>
        </h2>

        <div v-if="question.is_score" class="mt-6">
            <div class="grid gap-2" :style="{ gridTemplateColumns: `repeat(${scaleSize}, minmax(0, 1fr))` }">
                <button
                    v-for="n in scaleSize"
                    :key="n"
                    type="button"
                    class="score-tile"
                    :class="{ 'score-tile--selected': score === n && !notObserved, 'score-tile--accented': accentColor }"
                    @click="$emit('update:score', n)">
                    {{ n }}
                </button>
            </div>
            <div class="grid mt-2 details text-ink-muted" :style="{ gridTemplateColumns: `repeat(${scaleSize}, minmax(0, 1fr))` }">
                <div v-for="(label, i) in question.scale_labels.labels" :key="i" :class="labelAlign(i)">{{ label }}</div>
            </div>
        </div>

        <div v-if="showComposer" :class="question.is_score ? 'mt-6 pt-5 border-t border-border' : 'mt-4'">
            <div v-if="question.is_score" class="body-small-semibold text-ink mb-2">
                {{ t("components.assessment.shareExample") }} <span class="body-small text-ink-muted">{{ t("components.assessment.optional") }}</span>
            </div>
            <RaterTextComposer v-model="textModel" :rows="4" @update:busy="$emit('update:busy', $event)" @blur="$emit('blur')" />
        </div>

        <div v-if="allowNotObserved && question.is_score" class="mt-4">
            <button
                type="button"
                class="not-observed-pill"
                :class="{ 'not-observed-pill--selected': notObserved, 'not-observed-pill--accented': accentColor }"
                :aria-pressed="notObserved"
                @click="$emit('update:notObserved', !notObserved)">
                <i class="bi" :class="notObserved ? 'bi-check-circle-fill' : 'bi-slash-circle'" aria-hidden="true"></i>
                {{ notObservedCaption }}
            </button>
        </div>

        <p v-if="saveError" class="details text-status-error mt-4" role="alert">{{ saveError }}</p>
    </BaseCard>
</template>

<script setup lang="ts">
import type { ScaleLabels } from "~vue/api/generated/reflections";
import BaseCard from "~vue/components/BaseCard.vue";
import RaterTextComposer from "~vue/components/RaterTextComposer.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

export interface QuestionCardItem {
    id: string;
    title?: string;
    text: string;
    is_score: boolean;
    is_text: boolean;
    is_optional?: boolean;
    scale_labels: ScaleLabels;
}

interface Props {
    question: QuestionCardItem;
    score?: number | null;
    text: string;
    notObserved?: boolean;
    showUnanswered?: boolean;
    // LAB-798: when false, the "Share a specific example" composer is hidden on
    // SCORE (Likert) questions; open-ended TEXT questions always keep it.
    allowScoreExamples?: boolean;
    // LAB-794: when false, the "Not applicable — I haven't observed this" opt-out
    // is hidden.
    allowNotObserved?: boolean;
    // Optional accent color (any CSS color, so callers pass a token var) used
    // for the selected-score tile and the "not observed" pill. Purple is
    // Nadia's voice and must never leak into a reflection surface, so an
    // accent-less caller falls back to neutral ink, not to Valence primary.
    accentColor?: string;
    // Pastel fill for the selected score tile — the saturated accent is too heavy
    // behind the tile number, so it stays on hover/focus only.
    accentSurfaceColor?: string;
    // Set by callers that autosave: a failed save has to name the question it
    // belongs to, which a page-level message can't do when a whole section of
    // questions is on screen at once.
    saveError?: string;
}

const props = withDefaults(defineProps<Props>(), {
    notObserved: false,
    showUnanswered: false,
    allowScoreExamples: true,
    allowNotObserved: true,
});

// Open-ended questions always show their reflection composer; score questions
// only when examples are allowed for the template.
const showComposer = computed(() => !props.question.is_score || props.allowScoreExamples);

const accentStyle = computed(() => {
    if (!props.accentColor) {
        return undefined;
    }
    return {
        "--question-accent": props.accentColor,
        "--question-accent-surface": props.accentSurfaceColor ?? props.accentColor,
    };
});

const emit = defineEmits<{
    "update:score": [number];
    "update:text": [string];
    "update:notObserved": [boolean];
    "update:busy": [boolean];
    // The composer losing focus is a natural point to flush a debounced save.
    blur: [];
}>();

const scaleSize = computed(() => props.question.scale_labels.labels.length);
// LAB-878: some scales rename the opt-out (e.g. `effectiveness_4` uses
// "Not Observed / Not Applicable", `frequency_3` uses "Cannot Rate"). Falls
// back to the generic "Not applicable" copy for scales without an override.
const notObservedCaption = computed(() => {
    return props.question.scale_labels.not_observed_label ?? t("components.assessment.notObserved");
});

const textModel = computed({
    get: () => props.text,
    set: (v: string) => emit("update:text", v),
});

// Align the leftmost non-empty label left and the rightmost non-empty label
// right, matching how Image 15 anchors "Rarely" / "Consistently" to the ends.
function labelAlign(i: number): string {
    const labels = props.question.scale_labels.labels;
    if (i === 0 && labels[i]) {
        return "text-left";
    }
    if (i === labels.length - 1 && labels[i]) {
        return "text-right";
    }
    return "text-center";
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .score-tile {
        @apply flex items-center justify-center py-4 sm:py-5 rounded-lgr border border-control-border bg-control-bg body-bold text-ink transition-colors hover:border-control-border-strong focus-field;
    }

    .score-tile--selected {
        @apply bg-ink border-ink text-ink-inverse;
    }

    /* When the parent supplies --question-accent, the selected tile and the
       hover border switch to the flow's family color (amber / mint / pink).
       The focus halo stays on focus-field: it is how a keyboard user finds
       themselves on the page, so it has to look the same on every control
       rather than changing with the flow. Selected text stays dark so the
       pastel surfaces keep enough contrast. */
    .score-tile--accented {
        @apply hover:border-[color:var(--question-accent)];
    }
    .score-tile--accented.score-tile--selected {
        background-color: var(--question-accent-surface);
        border-color: var(--question-accent-surface);
        color: var(--color-ink);
    }

    .not-observed-pill {
        @apply inline-flex items-center gap-2 rounded-full border border-control-border bg-control-bg px-4 py-2 body-small text-ink-muted hover:border-control-border-strong hover:text-ink focus-field;
    }

    .not-observed-pill--selected {
        @apply bg-surface-sunken border-ink text-ink;
    }

    .not-observed-pill--accented {
        @apply hover:border-[color:var(--question-accent)];
    }
    .not-observed-pill--accented.not-observed-pill--selected {
        background-color: color-mix(in srgb, var(--question-accent) 25%, white);
        border-color: var(--question-accent);
        color: var(--color-ink);
    }
}
</style>
